import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { CanvasWidget } from "@projectstorm/react-canvas-core";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { ReactText, useContext, useEffect, useState } from "react";
import { useRouteMatch } from "react-router";
import { DialogComponent } from "../../../../../common/dialogContainers";
import { treeContext, TreeStatus } from "../../../schema";
import { TreeEngine } from "../../../TreeService";
import { TreeBlockDataSource } from "../../TreeBlockDataSource";
import { TreeBlockType } from "../../TreeBlockType";
import { AddTreeBlockView, SelectedTreeBlockView } from "../../views";
import { DefaultTreeBlockModel } from "../DefaultTreeBlockModel";
import { TrayWidget } from "../TrayWidget/TrayWidget";
import { OutputView } from "../OutputView/OutputView";
import { TreeCanvasWidget } from "../TreeCanvasWidget";
import { BodyWidgetHeader } from "./BodyWidgetHeader/BodyWidgetHeader";
import { PrepareTreeProgress } from "./PrepareTreeProgress";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    body: {
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: "550px",
      height: "86vh"
    },
    hidden: {
      visibility: "hidden"
    },
    content: {
      display: "flex",
      flexGrow: 1,
      position: "relative",
      maxHeight: "82vh",
      overflowY: "auto"
    },
    layer: {
      position: "relative",
      flexGrow: 1
    },
    canvas: {
      height: "100%",
      width: "100%",
      overflowY: "auto"
    },
    treeBlockPanel: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      position: "absolute",
      bottom: "0px",
      right: "0px",
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
      zIndex: 9,
      height: "30vh",
      width: "calc(100% - 200px)",
      overflowY: "auto"
    }
  })
);

interface BodyWidgetProps {
  treeEngine: TreeEngine;
  dataSource: TreeBlockDataSource<any, ReactText>;
}

export const BodyWidget = observer((props: BodyWidgetProps) => {
  const match = useRouteMatch();
  const tree = useContext(treeContext);
  const { treeEngine, dataSource } = props;
  const [selectedNode, setSelectedNode] = useState<DefaultTreeBlockModel>();
  const classes = useStyles();
  const [isPrepared, setIsPrepared] = useState(false);
  const [firstRender, setFirstRender] = useState<boolean>(true);
  const [addBlockOpen, setAddBlockOpen] = useState<boolean>(false);
  const [tempBlockPosition, setTempBlockPosition] = useState();
  const [currentTBlockType, setCurrentTBlockType] = useState(
    TreeBlockType.DESTINATION
  );

  useEffect(() => {
    setFirstRender(true);
    treeEngine.registerListeners(nodeRemoved, blockSelectionChanged);
    return () => dataSource.resetTreeStructure();
  }, []);

  useEffect(() => {
    firstRender ? initTree() : prepareTree();
    if (dataSource.treeStructure.blocks.length !== 0) {
      setFirstRender(false);
    }
  }, [dataSource.treeStructure]);

  const initTree = (): void => {
    setIsPrepared(false);
    prepareTree();
    treeEngine.autoDistribute();
    setIsPrepared(true);
    if (tree.oneDataSource.one?.status == TreeStatus.ACTIVE) {
      treeEngine.lockTree();
    }
  };

  const prepareTree = (): void => {
    const { blocks, connections } = dataSource.treeStructure;
    treeEngine.createOrUpdateRootBlock();
    treeEngine.clearConnections();
    treeEngine.initBlocks(blocks);
    treeEngine.initConnections(connections);
  };

  const resetBlockSelection = (): void => setSelectedNode(undefined);

  const blockSelectionChanged = ($event: any) => {
    $event.stopPropagation();
    if ($event.isSelected) {
      treeEngine.selectNode($event.entity.options.id);
      setSelectedNode($event.entity);
      setCurrentTBlockType($event.entity.options.name);
    } else {
      resetBlockSelection();
    }
  };

  const nodeRemoved = async (node: any) => {
    treeEngine.removeBlock(node);
    resetBlockSelection();
  };

  const onConfirmAddTreeBlock = (node: any = {}) => {
    if (!tempBlockPosition) {
      return;
    }
    treeEngine.addBlock(node, tempBlockPosition);
    setAddBlockOpen(false);
    setTempBlockPosition(undefined);
  };

  const onClose = (): void => {
    setAddBlockOpen(false);
  };

  const onDropBlockHandler = (event: any) => {
    const data = JSON.parse(event.dataTransfer.getData("storm-diagram-node"));
    setCurrentTBlockType(data.type);
    // @ts-ignore
    setTempBlockPosition(treeEngine.engine.getRelativeMousePoint(event));
    resetBlockSelection();
    setAddBlockOpen(true);
  };

  return (
    <div className={classes.body}>
      <BodyWidgetHeader
        tempStructure={dataSource.treeStructure}
        autoDistribute={treeEngine.autoDistribute}
      />
      <div className={classes.content}>
        <TrayWidget />
        {selectedNode && currentTBlockType !== TreeBlockType.ROOT && (
          <div className={classes.treeBlockPanel}>
            <SelectedTreeBlockView
              treeBlockId={selectedNode.getOptions().id}
              tBlockType={currentTBlockType}
              handleRefreshBlock={treeEngine.fetchTreeStructureChanges}
            />
            <OutputView
              selectedTreeBlockId={selectedNode.getOptions().id!}
              treeBlockType={currentTBlockType}
              handleAddOutput={treeEngine.addBlockOutput}
              handleRemoveOutput={treeEngine.removeBlockOutput}
            />
          </div>
        )}
        <div
          className={classes.layer}
          onDrop={event => onDropBlockHandler(event)}
          onDragOver={event => {
            event.preventDefault();
          }}
        >
          <TreeCanvasWidget>
            {!isPrepared && <PrepareTreeProgress />}
            <CanvasWidget
              className={clsx(classes.canvas, !isPrepared && classes.hidden)}
              engine={treeEngine.engine}
            />
          </TreeCanvasWidget>
        </div>
        <DialogComponent open={addBlockOpen} onCancel={onClose}>
          <AddTreeBlockView
            goBackHandler={onConfirmAddTreeBlock}
            match={match}
            dataSource={dataSource}
            tBlockType={currentTBlockType}
          />
        </DialogComponent>
      </div>
    </div>
  );
});
